import * as React from "react"
import { BREAKPOINTS } from '@/lib/constants';

type DeviceSize = 'iPhoneSE' | 'iPhonePro' | 'mobile' | 'tablet' | 'desktop';

/**
 * Custom hook to determine if the viewport is mobile-sized
 * Uses MediaQueryList for efficient updates without resize event listeners
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    // Initialize with SSR-friendly default
    if (typeof window === 'undefined') return false;
    return window.innerWidth < BREAKPOINTS.tablet;
  });

  React.useEffect(() => {
    // Use MediaQueryList for better performance
    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS.tablet - 1}px)`);
    
    // Update state based on media query
    const onChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
    };
    
    // Add event listener
    mql.addEventListener("change", onChange);
    
    // Set initial value
    setIsMobile(mql.matches);
    
    // Cleanup
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return isMobile;
}

/**
 * Custom hook to determine the current device size category
 * Uses ResizeObserver for efficient updates without resize event listeners
 */
export function useDeviceSize() {
  const [deviceSize, setDeviceSize] = React.useState<DeviceSize>(() => {
    // Initialize with SSR-friendly default
    if (typeof window === 'undefined') return 'desktop';
    
    const width = window.innerWidth;
    if (width <= BREAKPOINTS.iPhoneSE) return 'iPhoneSE';
    if (width <= BREAKPOINTS.iPhonePro) return 'iPhonePro';
    if (width <= BREAKPOINTS.mobile) return 'mobile';
    if (width <= BREAKPOINTS.tablet) return 'tablet';
    return 'desktop';
  });

  React.useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    
    // Create element to observe
    const observeElement = document.documentElement;
    
    // Throttle resize calculations for better performance
    let rafId: number | null = null;
    
    // Create ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      // Cancel any pending animation frame
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      
      // Use requestAnimationFrame to throttle updates
      rafId = window.requestAnimationFrame(() => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          
          // Calculate new device size
          let newSize: DeviceSize;
          if (width <= BREAKPOINTS.iPhoneSE) {
            newSize = 'iPhoneSE';
          } else if (width <= BREAKPOINTS.iPhonePro) {
            newSize = 'iPhonePro';
          } else if (width <= BREAKPOINTS.mobile) {
            newSize = 'mobile';
          } else if (width <= BREAKPOINTS.tablet) {
            newSize = 'tablet';
          } else {
            newSize = 'desktop';
          }
          
          // Update state only if changed
          setDeviceSize((prevSize) => prevSize !== newSize ? newSize : prevSize);
        }
        rafId = null;
      });
    });
    
    // Start observing
    resizeObserver.observe(observeElement);
    
    // Cleanup
    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, []);

  return deviceSize;
}